import { useEffect, useState } from 'react';
import type { DailyWeather, WeatherForecastResponse } from '@greeniq/shared';
import { getWeatherForecast } from '../api/weather.js';
import { DataCard } from './ui/data-card';

interface Props {
  courseSlug: string;
}

export function ForecastWidget({ courseSlug }: Props) {
  const [data, setData] = useState<WeatherForecastResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await getWeatherForecast(courseSlug);
        if (!cancelled) setData(resp);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseSlug]);

  if (error) return null;
  if (!data) return null;

  // Skip today — it's covered by the on-site card.
  const upcoming = data.daily.slice(1, 11);

  return (
    <DataCard
      className="mt-6"
      title="10-day forecast"
      subtitle={data.timezone}
    >
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-10">
        {upcoming.map((d) => (
          <DayTile key={d.date} day={d} />
        ))}
      </div>
    </DataCard>
  );
}

function DayTile({ day }: { day: DailyWeather }) {
  const date = new Date(day.date + 'T00:00:00');
  const weekday = date.toLocaleDateString(undefined, { weekday: 'short' });
  const md = date.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });

  return (
    <div className="overflow-hidden rounded-sm border border-border bg-bg-elevated p-2 text-center">
      <div className="truncate text-[10px] font-semibold uppercase tracking-[0.1em] text-fg-muted">
        {weekday}
      </div>
      <div className="truncate text-[10px] text-fg-muted">{md}</div>
      <div className="mt-1 truncate text-sm font-semibold text-fg">
        {fmt(day.tempMaxF)}°<span className="text-fg-muted">/{fmt(day.tempMinF)}°</span>
      </div>
      <div className="mt-0.5 flex items-baseline justify-center gap-1 text-[10px]">
        <span className="text-greeniq-400">
          {day.precipitationIn !== null && day.precipitationIn > 0
            ? `${day.precipitationIn.toFixed(2)}″`
            : '—'}
        </span>
        {day.precipitationProbabilityPct !== null && (
          <span className="text-fg-muted">{day.precipitationProbabilityPct}%</span>
        )}
      </div>
    </div>
  );
}

function fmt(v: number | null): string {
  return v === null ? '—' : Math.round(v).toString();
}
